import { useCallback, useEffect, useMemo } from 'react';
import type { ApolloError } from 'apollo-client';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import type { AssociatedField } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useFieldDetails as useFieldDetailsService } from '../../services/field-details/index.tsx';
import { toAssociatedField } from '../../services/field-details/utils.tsx';
import messages from './messages.tsx';

export type UseFieldDetailsReturn = {
	getFieldDetails: (fieldId: string) => void;
	fieldDetails?: AssociatedField;
	error?: ApolloError;
	loading: boolean;
	called: boolean;
};

export const FIELD_DETAILS_ERROR_FLAG_ID = toFlagId('field-details-error-flag');

export const useFieldDetails = (projectId: string, showErrorFlag = true): UseFieldDetailsReturn => {
	const {
		getFieldDetails: getFieldDetailsService,
		fieldDetails,
		loading,
		error,
		called,
	} = useFieldDetailsService();
	const { cloudId } = useTenantContext();
	const { showFlag, dismissFlag } = useFlagsService();

	const projectAri = useMemo(
		() =>
			createAri({
				resourceOwner: 'jira',
				cloudId,
				resourceType: 'project',
				resourceId: projectId,
			}),
		[projectId, cloudId],
	);
	const cloudAri = useMemo(
		() =>
			createAri({
				resourceOwner: 'jira',
				resourceType: 'site',
				resourceId: cloudId,
			}),
		[cloudId],
	);

	const getFieldDetails = useCallback(
		(fieldId: string) => {
			getFieldDetailsService({ fieldId, cloudId: cloudAri, projectId: projectAri });
		},
		[getFieldDetailsService, cloudAri, projectAri],
	);

	const associatedFieldDetails = useMemo(() => toAssociatedField(fieldDetails), [fieldDetails]);

	useEffect(() => {
		if (!showErrorFlag) {
			return;
		}

		if (error) {
			showFlag({
				id: FIELD_DETAILS_ERROR_FLAG_ID,
				type: 'error',
				title: messages.errorTitle,
				description: messages.errorMessage,
			});
		} else {
			dismissFlag(FIELD_DETAILS_ERROR_FLAG_ID);
		}
	}, [error, showFlag, dismissFlag, showErrorFlag]);

	return {
		getFieldDetails,
		fieldDetails: associatedFieldDetails,
		loading,
		error,
		called,
	};
};
